import { usePagination } from '@mui/lab';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const navButtonClass =
  'inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-[#021744] px-3.5 text-sm font-medium text-[#021744] transition-colors duration-200 hover:bg-[#021744] hover:text-white disabled:pointer-events-none disabled:opacity-40 sm:px-5';

const pageButtonClass =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border px-3 text-sm font-medium transition-colors duration-200 disabled:pointer-events-none disabled:opacity-40';

function PageButton({ selected, page, ...item }) {
  return (
    <button
      type="button"
      aria-label={`Page ${page}`}
      aria-current={selected ? 'page' : undefined}
      className={`${pageButtonClass} ${
        selected
          ? 'border-[#021744] bg-[#021744] text-white'
          : 'border-gray-200 bg-white text-[#021744] hover:border-[#021744]'
      }`}
      {...item}
    >
      {page}
    </button>
  );
}

function NavButton({ type, ...item }) {
  const isPrevious = type === 'previous';
  const label = isPrevious ? 'Previous' : 'Next';

  return (
    <button
      type="button"
      aria-label={label}
      className={navButtonClass}
      {...item}
    >
      {isPrevious ? <FaChevronLeft className="shrink-0 text-xs" /> : null}
      <span className="hidden sm:inline">{label}</span>
      {!isPrevious ? <FaChevronRight className="shrink-0 text-xs" /> : null}
    </button>
  );
}

function Ellipsis() {
  return (
    <span
      className="inline-flex min-h-11 min-w-9 items-center justify-center text-[#949494]"
      aria-hidden
    >
      …
    </span>
  );
}

export function HubPagination({ count, page, onChange, className = '' }) {
  const { items } = usePagination({
    count: Math.max(1, count),
    page,
    onChange,
    siblingCount: 1,
    boundaryCount: 1,
  });

  const previous = items.find((item) => item.type === 'previous');
  const next = items.find((item) => item.type === 'next');
  const pageItems = items.filter(
    (item) => item.type !== 'previous' && item.type !== 'next',
  );

  return (
    <nav
      aria-label="Pagination"
      className={`w-full ${className}`.trim()}
    >
      <div className="flex w-full items-center justify-between gap-2 sm:gap-4">
        {previous ? <NavButton {...previous} /> : <span className="min-w-[44px] sm:min-w-[120px]" />}

        <ul className="flex min-w-0 flex-1 items-center justify-center gap-1 overflow-x-auto px-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-1.5 [&::-webkit-scrollbar]:hidden">
          {pageItems.map((item, index) => {
            const { type, page: pageNumber, selected, ...itemProps } = item;

            if (type === 'start-ellipsis' || type === 'end-ellipsis') {
              return (
                <li key={`ellipsis-${index}`}>
                  <Ellipsis />
                </li>
              );
            }

            return (
              <li key={pageNumber ?? index}>
                <PageButton page={pageNumber} selected={selected} {...itemProps} />
              </li>
            );
          })}
        </ul>

        {next ? <NavButton {...next} /> : <span className="min-w-[44px] sm:min-w-[120px]" />}
      </div>
    </nav>
  );
}
